import { useState, type ReactNode } from 'react';
import { ArrowRight, Check, Copy, FileStack, Forward, Paperclip, PauseCircle } from 'lucide-react';
import type { PaigeEmailSource } from './paige.global';
import { trackProduct } from './product-tracker';

/**
 * The address tab: the address and one button to copy it, then how email becomes documents as
 * three pictures rather than a paragraph, and the one line on forwarding automatically.
 */
export function EmailAddressTab({
  source,
  saving,
  onRelease,
}: {
  source: PaigeEmailSource;
  saving: boolean;
  onRelease: () => void;
}): ReactNode {
  if (!source.enabled)
    return (
      <p className="paige-email-sheet-quiet">
        Email is off, so mail to this job is refused. Switch it on above; the address stays the
        same.
      </p>
    );
  return (
    <>
      <Address address={source.address} />
      {source.held && <Held saving={saving} onRelease={onRelease} />}
      <ol className="paige-email-sheet-steps" aria-label="How it works">
        <Step icon={<Forward size={22} />} title="Send or forward" detail="From any mailbox" />
        <Arrow />
        <Step
          icon={<Paperclip size={22} />}
          title="Attachments read"
          detail="PDF, TIFF, images, ZIP"
        />
        <Arrow />
        <Step
          icon={<FileStack size={22} />}
          title="Documents here"
          detail="Logos and signatures left out"
        />
      </ol>
      <p className="paige-email-sheet-tip">Forward automatically with an Outlook rule or Gmail.</p>
    </>
  );
}

/** A held address: its mail waits, and one button lets it in. */
function Held({ saving, onRelease }: { saving: boolean; onRelease: () => void }): ReactNode {
  return (
    <p className="paige-email-sheet-held" role="status">
      <PauseCircle size={18} aria-hidden="true" />
      <span>On hold: mail to this address is kept and waits.</span>
      <button type="button" disabled={saving} onClick={onRelease}>
        Release
      </button>
    </p>
  );
}

function Step({
  icon,
  title,
  detail,
}: {
  icon: ReactNode;
  title: string;
  detail: string;
}): ReactNode {
  return (
    <li>
      <span className="paige-email-sheet-step-icon" aria-hidden="true">
        {icon}
      </span>
      <strong>{title}</strong>
      <small>{detail}</small>
    </li>
  );
}

function Arrow(): ReactNode {
  return (
    <li className="paige-email-sheet-step-arrow" aria-hidden="true">
      <ArrowRight size={18} />
    </li>
  );
}

/** The address, large, and one button that copies it. */
function Address({ address }: { address: string }): ReactNode {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(address);
    trackProduct('source.email_copied');
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="paige-email-sheet-address">
      <code>{address}</code>
      <button type="button" onClick={() => void copy()}>
        {copied ? <Check size={16} aria-hidden="true" /> : <Copy size={16} aria-hidden="true" />}
        {copied ? 'Copied' : 'Copy address'}
      </button>
    </div>
  );
}
